import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getEstadisticas } from '@/utils/api';
import { ArrowLeft, BarChart3, Loader2, AlertCircle, ChevronLeft, ChevronRight, Activity, Download } from 'lucide-react';
import { generateStatsPdf } from '@/utils/generateStatsPdf';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_9c5cf9e4-7ffa-44c2-89bf-7aacf8535caa/artifacts/4q3hto4h_634037357_1389521753189000_3144761508300406294_n.jpg';

const CATEGORIAS = [
    'Embarazos con traslado', 'Embarazos sin traslados', 'Atendidos en base con traslado', 'Atendidos en base sin traslado',
    'Atropellados sin traslado', 'Atropellados con traslado', 'Choques con lesionados con traslado', 'Choques con lesionados sin traslado',
    'Desbarrancados con traslado', 'Desbarrancados sin traslado', 'Enfermos trasladados', 'Enfermos estabilizados en su domicilio',
    'Pacientes estado etilico / otros', 'Lesionados (Riñas, caidas, otros) con traslado', 'Lesionados (Riñas, caidas, otros) sin traslado',
    'Prensados con traslado', 'Prensados sin traslado', 'Intentos de suicidios', 'Suicidios concretados',
    'Traslados locales', 'Traslados foraneos', 'Violaciones', 'Volcaduras con lesionados con traslado', 'Volcaduras con lesionados sin traslado',
    'Busquedas', 'Fallecidos', 'Eventos locales', 'Eventos foraneos', 'Servicios cancelados en el trayecto', 'Sin Categoría'
];

export default function Estadisticas() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // By default, current month
    const today = new Date();
    const [currentMonth, setCurrentMonth] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);

    useEffect(() => {
        if (user?.role !== 'admin') {
            navigate('/dashboard');
            return;
        }
        const load = async () => {
            setLoading(true);
            try {
                const stats = await getEstadisticas();
                setData(stats);
            } catch (err) {
                setError('Error cargando estadísticas');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [user, navigate]);

    // Manejar cambio de meses
    const changeMonth = (offset) => {
        const [y, m] = currentMonth.split('-').map(Number);
        const d = new Date(y, m - 1 + offset, 1);
        setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    };

    // Filtrar y agrupar por mes actual
    const monthData = useMemo(() => {
        const stats = {};
        CATEGORIAS.forEach(c => stats[c] = 0);

        // sum total
        let total = 0;
        data.forEach(row => {
            if (row.mes === currentMonth) {
                const c = row.servicio || 'Sin Categoría';
                if (stats[c] !== undefined) {
                    stats[c] += row.conteo;
                    total += row.conteo;
                }
            }
        });
        return { list: stats, total };
    }, [data, currentMonth]);

    // Formato agradable para el título del mes
    const formatMonth = (ym) => {
        const [y, m] = ym.split('-');
        const date = new Date(y, m - 1, 1);
        const mStr = date.toLocaleString('es-MX', { month: 'long' });
        return `${mStr.charAt(0).toUpperCase() + mStr.slice(1)} ${y}`;
    };

    const handleExportPdf = async () => {
        try {
            const mStr = formatMonth(currentMonth);
            await generateStatsPdf(mStr, monthData, CATEGORIAS);
        } catch (error) {
            console.error('Error generando PDF:', error);
            setError('Error al generar el PDF de estadísticas');
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 pb-20" style={{ fontFamily: 'Inter, sans-serif' }}>
            {/* Sticky Header */}
            <header className="bg-[#1a3a5c] border-b border-white/10 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1 text-slate-300 hover:text-white text-sm transition-colors mr-2">
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                        <img src={LOGO_URL} alt="Logo" className="h-9 w-9 rounded-full object-cover" />
                        <div>
                            <p className="text-white font-black text-sm leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>FRAP Estadísticas</p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-8">
                <div className="flex items-center gap-2 mb-6">
                    <BarChart3 className="w-6 h-6 text-[#1a7a4a]" />
                    <h1 className="text-2xl font-black text-[#1a3a5c]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Reporte de Servicios
                    </h1>
                </div>

                {error && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-4">
                        <AlertCircle className="w-4 h-4" />{error}
                    </div>
                )}

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    {/* Navegador de mes */}
                    <div className="bg-slate-100 flex items-center justify-between px-6 py-4 border-b border-slate-200">
                        <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                            <ChevronLeft className="w-5 h-5 text-slate-600" />
                        </button>
                        <h2 className="text-lg font-bold text-slate-800 w-48 text-center">{formatMonth(currentMonth)}</h2>
                        <button onClick={() => changeMonth(1)} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                            <ChevronRight className="w-5 h-5 text-slate-600" />
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex flex-col items-center py-20 text-slate-400">
                            <Loader2 className="w-8 h-8 animate-spin mb-4 text-[#1a7a4a]" />
                            <p className="text-sm">Analizando datos...</p>
                        </div>
                    ) : (
                        <div className="p-6">
                            {/* Tarjeta de Total */}
                            <div className="bg-gradient-to-br from-[#1a7a4a] to-[#125835] rounded-xl text-white p-6 flex items-center justify-between shadow-md mb-8">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-white/20 rounded-lg">
                                        <Activity className="w-8 h-8" />
                                    </div>
                                    <div>
                                        <p className="text-white/80 font-bold text-sm uppercase tracking-wider mb-1">Total del Mes</p>
                                        <p className="text-4xl font-black">{monthData.total || 0}</p>
                                    </div>
                                </div>
                                <div>
                                    <button
                                        onClick={handleExportPdf}
                                        className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white font-bold py-2 px-4 rounded-lg transition-colors border border-white/20"
                                    >
                                        <Download className="w-4 h-4" />
                                        <span>Exportar PDF</span>
                                    </button>
                                </div>
                            </div>

                            {/* Lista Desglosada */}
                            <div className="space-y-0 text-sm">
                                <div className="grid grid-cols-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest px-4 pb-2 border-b border-slate-200">
                                    <div className="col-span-4">Categoría</div>
                                    <div className="col-span-1 text-right">Cantidad</div>
                                </div>
                                {CATEGORIAS.map((cat, idx) => {
                                    const val = monthData.list[cat] || 0;
                                    // Si no hay valores en este mes y queremos mantenerlo limpio, podemos decidir ocultar los 0s o mostrarlos suaves.
                                    // Mostraremos todos para cumplir con evaluar cada categoria por mes (o si el usuario prefiere se podrían ocultar los que estan en 0, pero tener visible la plantilla es mejor).

                                    return (
                                        <div
                                            key={cat}
                                            className={`grid grid-cols-5 px-4 py-3 items-center border-b border-slate-100 transition-colors ${val > 0 ? 'bg-green-50/30' : ''}`}
                                        >
                                            <div className={`col-span-4 font-medium ${val > 0 ? 'text-[#1a3a5c]' : 'text-slate-400'}`}>
                                                {cat}
                                            </div>
                                            <div className={`col-span-1 text-right font-bold ${val > 0 ? 'text-[#1a7a4a] text-base' : 'text-slate-300'}`}>
                                                {val}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
